import { createContext, useContext } from 'react';

export type SupportedLanguage = 'en' | 'es' | 'fr' | 'pt' | 'ar' | 'de' | 'ja' | 'ko' | 'zh-CN' | 'zh-TW' | 'ru' | 'id' | 'tr' | 'vi' | 'hi';

export interface LanguageOption {
  code: SupportedLanguage;
  name: string;
  nativeName: string;
  direction: 'ltr' | 'rtl';
}

export const SUPPORTED_LANGUAGES: LanguageOption[] = [
  { code: 'en', name: 'English', nativeName: 'English', direction: 'ltr' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', direction: 'ltr' },
  { code: 'fr', name: 'French', nativeName: 'Français', direction: 'ltr' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', direction: 'ltr' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', direction: 'rtl' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', direction: 'ltr' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', direction: 'ltr' },
  { code: 'ko', name: 'Korean', nativeName: '한국어', direction: 'ltr' },
  { code: 'zh-CN', name: 'Simplified Chinese', nativeName: '简体中文', direction: 'ltr' },
  { code: 'zh-TW', name: 'Traditional Chinese', nativeName: '繁體中文', direction: 'ltr' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', direction: 'ltr' },
  { code: 'id', name: 'Indonesian', nativeName: 'Bahasa Indonesia', direction: 'ltr' },
  { code: 'tr', name: 'Turkish', nativeName: 'Türkçe', direction: 'ltr' },
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', direction: 'ltr' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', direction: 'ltr' }
];

export const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

/**
 * 全局变量，存储当前语言
 * 用于在非React组件中访问当前语言
 */
let globalCurrentLanguage: SupportedLanguage = DEFAULT_LANGUAGE;

/**
 * 初始化时尝试从localStorage获取语言
 */
try {
  const savedLang = localStorage.getItem('preferredLanguage');
  if (savedLang && SUPPORTED_LANGUAGES.some(lang => lang.code === savedLang)) {
    globalCurrentLanguage = savedLang as SupportedLanguage;
  } else {
    // 尝试从浏览器语言设置中获取
    const browserLang = navigator.language;
    const exactMatch = SUPPORTED_LANGUAGES.find(lang => lang.code === browserLang);
    if (exactMatch) {
      globalCurrentLanguage = exactMatch.code;
    } else {
      // 检查语言的主要部分是否匹配（例如 'zh-CN' 匹配 'zh'）
      const mainLang = browserLang.split('-')[0];
      const partialMatch = SUPPORTED_LANGUAGES.find(lang => 
        lang.code === mainLang || lang.code.startsWith(`${mainLang}-`)
      );
      if (partialMatch) {
        globalCurrentLanguage = partialMatch.code;
      }
    }
  }
} catch (error) {
  console.error('初始化语言失败，使用默认语言:', error);
}

/**
 * 获取当前语言（非Hook版本，可以在任何地方使用）
 * @returns 当前语言代码
 */
export const getCurrentLanguage = (): SupportedLanguage => {
  return globalCurrentLanguage;
};

/**
 * 设置当前语言（非Hook版本，可以在任何地方使用）
 * @param language 要设置的语言
 */
export const setCurrentLanguage = (language: SupportedLanguage): void => {
  globalCurrentLanguage = language;
  try {
    localStorage.setItem("preferredLanguage", language);
  } catch (error) {
    console.error('保存语言偏好失败:', error);
  }
};

// Create a context for language management
interface LanguageContextType {
  currentLanguage: SupportedLanguage;
  setLanguage: (lang: SupportedLanguage) => void;
  t: (key: string, params?: Record<string, any>) => string;
}

export const LanguageContext = createContext<LanguageContextType>({
  currentLanguage: DEFAULT_LANGUAGE,
  setLanguage: () => {},
  t: (key) => key,
});

export const useLanguage = () => useContext(LanguageContext);
